import * as React from "react"
import {createStorefrontApiClient} from '@shopify/storefront-api-client'
import { toast } from "react-toastify"
import { isEmpty } from "lodash"
import { 
	createCart, 
	fetchCart, 
	addToCartQuery, 
	removeItemFromCartQuery, 
	updateItemToCartQuery,
	updateNoteToCartQuery,
	cartBuyerIdentityUpdateQuery,
 } from "../queries/shopify/storefront/cart"


const storefrontClient = createStorefrontApiClient({
  storeDomain: process.env.GATSBY_SHOPIFY_STORE_URL,
  apiVersion: process.env.GATSBY_SHOPIFY_STOREFRONT_API_VERSION,
  publicAccessToken: process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
})

const defaultValues = {
  isOpen: false,
  loading: false,
  onOpen: () => {},
  onClose: () => {},
  addVariantToCart: () => {},
  addMultiVariantsToCart: () => {},
  removeLineItem: () => {},
  updateLineItem: () => {},
  updateOrderNote: () => {},
  storefrontClient,
  checkout: {
    lineItems: [],
  },
  cart: {},
  customerAccessToken: null,
  isCartExists: () => {},
  isCustomerAuthenticated: false,
}

export const StoreContext = React.createContext(defaultValues)

const isBrowser = typeof window !== `undefined`
const localCartKey = `shopify_cart_id`

export const StoreProvider = ({ children }) => {
	
  const getlocalStorage = (value) => {
        try {
            return JSON.parse(localStorage.getItem(value))
        } catch (e) {
            return ''
        }
    }
	
	const setlocalStorage = (key, value) => {
		if (isBrowser && key && value) {
			localStorage.setItem(key, value)
		}
	}

  const isCustomerTokenValid = (customerAccessToken) => {
	if (customerAccessToken ) {
		const isAuthenticated = (customerAccessToken.expiresAt && new Date(customerAccessToken.expiresAt).getTime() > new Date().getTime()) ? true : false
		return isAuthenticated
	} else {
		return false
	}
  }
	
  const [cart, setCart] = React.useState(defaultValues.cart)
  const [isCartAttachToUser, setCartAttachToUser] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [didJustAddToCart, setDidJustAddToCart] = React.useState(false)
  const [customerAccessToken, setCustomerAccessToken] = React.useState(getlocalStorage('customerAccessToken'))
  const [userAccessToken, setUserAccessToken] = React.useState(getlocalStorage('customerAccessToken')?.accessToken)
  const [customer, setCustomer] = React.useState({})
  const [isCustomerAuthenticated, setIsCustomerAuthenticated] = React.useState(isCustomerTokenValid(customerAccessToken))
  const [customerStatus, setCustomerStatus] = React.useState(false)
  const [customerRole, setCustomerRole] = React.useState(null)
  const [customerCompany, setCustomerCompany] = React.useState({})
  const [appMessage, setAppMessage] = React.useState(getlocalStorage('appMessage'))
  
  const setCartItem = async (cart) => {
	if (isBrowser) {
	  localStorage.setItem(localCartKey, cart.id)
	}
	
	setCart(cart)

  }
  
  const updateCustomerAccessToken = (value) => {
	isBrowser && localStorage.setItem('customerAccessToken', JSON.stringify(value))
    setCustomerAccessToken(value)
    setUserAccessToken(value?.accessToken)
	
	if (value) {
		setIsCustomerAuthenticated(isCustomerTokenValid(value))
	} else {
		setIsCustomerAuthenticated(false)
	}
  }
  
  const addAppMessage = (message, type = "success") => {
	if (message) {
		isBrowser && localStorage.setItem('appMessage', JSON.stringify({message: message, type: type}))
		setAppMessage({message: message, type: type})
	}
  }
  
  const clearAppMessage = () => {
  	isBrowser && localStorage.removeItem('appMessage')
	setAppMessage(null)
  }
  
  const initializeCart = async () => {
//console.log('cart init')
    const existingCartID = isBrowser
      ? localStorage.getItem(localCartKey)
      : null

    if (!isEmpty(existingCartID)) {
      try {
//console.log('cart iniated: ',existingCartID)
  		const {data, errors} = await storefrontClient.request(fetchCart, {
  		  variables: {
  		    id: existingCartID,
  		  }
  		})

  		if (!errors && data.cart) {
  		  setCartItem(data.cart)
  		}

      } catch (e) {
        localStorage.setItem(localCartKey, null)
      }
    } else {
		const {data, errors} = await storefrontClient.request(createCart, {
		  	variables: {
		  	  input: {},
		  	},
	    })
	  	
	    if (!errors && data.cartCreate.cart) {
	  		setCartItem(data.cartCreate.cart)
	    }
	}
  }
  
  React.useEffect(() => {
	  if (isEmpty(cart)) {
	  	initializeCart()
	  }

  }, [])
  
  const processAndSetCompany = (company) => {
  	let companyToSave = {
  		id: company.value
  	}
  	
  	if (company.reference) {
  		companyToSave['handle'] = company.reference.handle
  		companyToSave['status'] = company.reference.status ? company.reference.status.value : false
		companyToSave['name'] = company.reference.name ? company.reference.name.value : ''
		companyToSave['customers'] = company.reference.customers ? JSON.parse(company.reference.customers.value) : []
		companyToSave['vip_membership'] = company.reference.vip_membership ? company.reference.vip_membership.value : false
		companyToSave['net_terms_payment_enable'] = company.reference.net_terms_payment_enable ? company.reference.net_terms_payment_enable.value : false
		companyToSave['net_payment_terms'] = company.reference.net_payment_terms ? company.reference.net_payment_terms.value : false
		companyToSave['company_owner'] = company.reference.company_owner ? company.reference.company_owner.value : false
  	}
  	
  	setCustomerCompany(companyToSave)
  }
  
  const initializeCustomer = async (force = false) => {
//console.log(customer)
  	if (customerAccessToken && userAccessToken && isEmpty(customer)) {
  		
  		if (isCustomerTokenValid(customerAccessToken)) {

  			const response = await fetch("/api/getCustomerDetails", {
  	          method: "POST", 
  	          headers: {
  	            "Content-Type": "application/json",
  				accessToken: userAccessToken,
  	          },
  	        })
  						
  			if (response.ok) {
  			  const data = await response.json()
  			  
  			  if (data.status && data.customer) {
//console.log('customer inited')
  				setCustomer(data.customer)
  				
  				if (data.customer.role?.value) {
  					setCustomerRole(data.customer.role.value)
  				}
  				
  				if (data.customer.status?.value) {
  					setCustomerStatus(data.customer.status.value)
  				}
  				
  				if (data.customer.company?.value) {
  					processAndSetCompany(data.customer.company)
  				}
				
  			  } else {
  				setIsCustomerAuthenticated(false)
  			  }

  			} 
  		} else {
  			setIsCustomerAuthenticated(false)
  		}
  		
  	} else {
  		setIsCustomerAuthenticated(false)
  	}
  	
  }
  
  React.useEffect(() => {
	if (isEmpty(customer)) {
		initializeCustomer()	
	}
  	
  }, [customer])

  /*const attachCustomerWithCart = React.useCallback( async (userAccessToken, cartId) => {
	if(!isCartAttachToUser) {
		await storefrontClient.request(cartBuyerIdentityUpdateQuery, {
	        variables: {
	          cartId: cartId,
	          buyerIdentity: {"customerAccessToken": userAccessToken},
	        },
	      }).then((res) => {
			if (res.data) {
				setCartAttachToUser(true)
			}
		  })
	}
	
  }, [isCartAttachToUser])
  
  React.useEffect(() => {
	
	if (userAccessToken && customer && cart.id) {
		attachCustomerWithCart(userAccessToken, cart.id)	
	}
  	
  }, [customer, cart, userAccessToken, attachCustomerWithCart])*/
  
  const isCartExists = (cartId) => {

	if (cartId !== `null`) {
		try {

			return storefrontClient.request(fetchCart, {
			  variables: {
			    id: cartId,
			  }
			}).then	((res) => {
		  		if (res.data.cart === null) {
		  			return false
		  		} else {
					return true
				}
		  	})

        } catch (e) {
          //localStorage.setItem(localCartKey, null)
        }
	
	} else {
		return false
	}
  }
  
  const addVariantToCart = (variantId, quantity, attributes = '') => {
    setLoading(true)

    let cartID = cart.id
  	let lineItemsToUpdate = ''
	
	if (isEmpty(cartID)) {
		cartID = isBrowser ? localStorage.getItem(localCartKey) : null
	}
	
//console.log(cartID)
	
	if (isEmpty(cartID)) {
		setLoading(false)
		toast.error("Something went wrong! Item not added.")
		return
	}
	
  	if (attributes) {
  	 	lineItemsToUpdate = [
  	      {
  	        merchandiseId: variantId,
  	        quantity: parseInt(quantity, 10),
  			attributes: attributes

  	      },
  	    ]
  	} else {
  	 	lineItemsToUpdate = [
  	      {
  	        merchandiseId: variantId,
  	        quantity: parseInt(quantity, 10),
  	      },
  	    ]
  	}

	return storefrontClient.request(addToCartQuery, {
	  variables: {
	    cartId: cartID,
		lines: lineItemsToUpdate
	  }
	}).then((res) => {
		if (res.data) {
			setCart(res.data.cartLinesAdd.cart)
			setLoading(false)
			//setDidJustAddToCart(true)
			//setTimeout(() => setDidJustAddToCart(false), 3000)
			toast.success("The item successfully added to cart.")
		} else {
			setLoading(false)
			toast.error("Something went wrong! Item not added.")
		}
	})
  }  
  
  const addMultiVariantsToCart = (variants) => {
    setLoading(true)

    let cartID = cart.id
    let lineItemsToUpdate = []
	
	if (isEmpty(cartID)) {
		cartID = isBrowser ? localStorage.getItem(localCartKey) : null
	}
	
	if (isEmpty(cartID)) {
		setLoading(false)
		toast.error("Something went wrong! Item not added.")
		return
	}
  	
  	variants.forEach((variant) => {
  		let itemToAdd = ''
  		
  		if (variant.customAttributes) {
  	  	 	itemToAdd = {
  	  	        merchandiseId: variant.variantId,
  	  	        quantity: parseInt(variant.quantity, 10),
  	  			attributes: variant.customAttributes
  	  	     }
  	  	} else {
  			itemToAdd = {
  	  	        merchandiseId: variant.variantId,
  	  	        quantity: parseInt(variant.quantity, 10),
  	  	     }
  	  	}
  		lineItemsToUpdate.push(itemToAdd)
  	})
	
	return storefrontClient.request(addToCartQuery, {
	  variables: {
	    cartId: cartID,
		lines: lineItemsToUpdate
	  }
	}).then((res) => {
		if (res.data) {
			setCart(res.data.cartLinesAdd.cart)
			setLoading(false)
			//setDidJustAddToCart(true)
			//setTimeout(() => setDidJustAddToCart(false), 3000)
			toast.success("The item successfully added to cart.")
		} else {
			setLoading(false)
			toast.error("Something went wrong! Item not added.")
		}

	})
  }
  
  const removeLineItem = (cartID, lineItemID) => {
    setLoading(true)

	return storefrontClient.request(removeItemFromCartQuery, {
	  variables: {
	    cartId: cartID,
		lineIds: lineItemID
	  }
	}).then((res) => {
		if (res.data) {
			setCart(res.data.cartLinesRemove.cart)
			setLoading(false)
			toast.warning("The item removed from cart.")
		} else {
			setLoading(false)
			toast.error("Something went wrong! Item not removed.")
		}
	})
  }
  
  const updateLineItem = (cartID, lineItemID, quantity) => {
	setLoading(true)
	
	const lineItemsToUpdate = [
		{ id: lineItemID, quantity: parseInt(quantity, 10) },
	]
	
	return storefrontClient.request(updateItemToCartQuery, {
	  variables: {
	    cartId: cartID,
		lines: lineItemsToUpdate
	  }
	}).then((res) => {
		if (res.data) {
			setCart(res.data.cartLinesUpdate.cart)
			setLoading(false)
			toast.warning("The cart item updated.")
		} else {
			setLoading(false)
			toast.error("Something went wrong! Item not updated.")
		}
	})
  }
  
  const updateOrderNote = (cartID, note) => {
	setLoading(true)
		
	return storefrontClient.request(updateNoteToCartQuery, {
	  variables: {
	    cartId: cartID,
		note: note
	  }
	}).then((res) => {
		if (res.data) {
			setCart(res.data.cartNoteUpdate.cart)
			setLoading(false)
			toast.success("The order note updated.")
		}
	})
  }
  
  const logoutCustomer = async () => {
	
	const logoutRequestUrl = new URL(
	  `https://shopify.com/authentication/${process.env.GATSBY_SHOPIFY_STORE_ID}/logout`
	)

	logoutRequestUrl.searchParams.append(
	  'id_token_hint',
	  customerAccessToken.id_token
	)
	logoutRequestUrl.searchParams.append(
	  'post_logout_redirect_uri',
	  process.env.GATSBY_SHOPIFY_LOGOUT_REDIRECT_URL
	)
	
	updateCustomerAccessToken(null)
	setIsCustomerAuthenticated(false)
	localStorage.removeItem("code-verifier")
	setCartAttachToUser(false)
	setCustomer({})
	setCustomerStatus(false)
	setCustomerRole(null)
	setCustomerCompany({})
	
	window.location.href = logoutRequestUrl.toString()

  }
  
  return (
    <StoreContext.Provider
      value={{
        ...defaultValues,
        addVariantToCart,
		addMultiVariantsToCart,
        removeLineItem,
        updateLineItem,
		updateOrderNote,
		cart,
		isCartAttachToUser,
        loading,
        didJustAddToCart,
        customerAccessToken,
        updateCustomerAccessToken,
        userAccessToken,
		customer,
		setCustomer,
		logoutCustomer,
		isCustomerAuthenticated,
		isCartExists,
		customerStatus,
		customerRole,
		customerCompany,
		processAndSetCompany,
		addAppMessage,
		clearAppMessage,
		appMessage,
		setlocalStorage,
		getlocalStorage,
		initializeCustomer
      }}
    >
      {children}
    </StoreContext.Provider>
  )
}
